/**
 * The mount. Both SPAs are identical here, and that is the point: one stack, one theme, one
 * provider shell (adr/0007-web-app-stack.md). The cabinet's is `CabinetProviders`, the same shell
 * speaking the cabinet's language.
 *
 * StrictMode is on. It double-invokes effects in development, which is what surfaces an effect
 * that is not idempotent - and the theme swap and DataTable's roving focus both run in effects.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { CabinetProviders } from './features/shell/CabinetProviders';
import { startCabinetLanguage } from './features/shell/cabinet-language';
import { router } from './router';

const container = document.getElementById('root');

if (!container) {
  // A missing #root means index.html and this file have drifted apart. Say so rather than
  // rendering nothing, which looks exactly like the white page a wrong `base` produces.
  throw new Error('index.html has no #root element; the app cannot mount.');
}

// The router sits INSIDE the providers, not outside them: every screen it renders needs the Mantine
// theme and the colour scheme, and a provider below the router would remount on navigation.
function mount(root: HTMLElement): void {
  createRoot(root).render(
    <StrictMode>
      <CabinetProviders>
        <RouterProvider router={router} />
      </CabinetProviders>
    </StrictMode>,
  );
}

// The language first: its files arrive before the first paint, so a German page never flashes
// English (features/shell/cabinet-language.ts).
void startCabinetLanguage().then(
  () => {
    mount(container);
  },
  () => {
    mount(container);
  },
);
